// These values mirror the current DTO, not future provider capabilities.
export const COMMUNICATION_EVENTS = [
  'BOOKING_CONFIRMATION', 'BOOKING_CHANGE', 'BOOKING_CANCELLATION', 'APPOINTMENT_REMINDER',
  'WAITLIST_AVAILABILITY', 'VOUCHER_ISSUED', 'VOUCHER_EXPIRY', 'PACKAGE_ISSUED', 'PACKAGE_EXPIRY', 'PACKAGE_LOW_CREDITS', 'FOLLOW_UP',
];
export const COMMUNICATION_CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP'];
// Release actions follow the handoff; history still recognizes all legacy values.
export const EMAIL_RELEASE_EVENTS = COMMUNICATION_EVENTS.filter((event) => !['VOUCHER_ISSUED', 'PACKAGE_ISSUED', 'PACKAGE_EXPIRY'].includes(event));
export const COMMUNICATION_LANGUAGES = ['IT', 'EN'];
export const COMMUNICATION_STATUSES = ['PENDING', 'SENDING', 'SENT', 'FAILED', 'SKIPPED', 'UNAVAILABLE', 'CANCELLED'];
export const COMMUNICATION_PLACEHOLDERS = ['clientName', 'clientSurname', 'treatmentName', 'startAt', 'endAt'];
export const APPOINTMENT_EMAIL_EVENTS = [
  'BOOKING_CONFIRMATION', 'BOOKING_CHANGE', 'BOOKING_CANCELLATION', 'APPOINTMENT_REMINDER', 'FOLLOW_UP',
];

export const COMMUNICATION_TARGETS = {
  appointmentId: APPOINTMENT_EMAIL_EVENTS,
  waitlistOfferId: ['WAITLIST_AVAILABILITY'],
  storedValueInstrumentId: ['VOUCHER_EXPIRY'],
  packageEntitlementId: ['PACKAGE_LOW_CREDITS'],
};
export const COMMUNICATION_RULE_LIMITS = {
  reminderMinutesBefore: [1, 525600], followUpMinutesAfter: [1, 525600],
  voucherExpiryNoticeDays: [1, 3650], packageExpiryNoticeDays: [1, 3650],
  packageLowCreditThreshold: [0, 100000], maxAttempts: [1, 20], retryMinutes: [1, 1440], stuckMinutes: [1, 1440],
};
export const COMMUNICATION_PLACEHOLDER_GROUPS = {
  appointment: COMMUNICATION_PLACEHOLDERS,
  waitlist: [...COMMUNICATION_PLACEHOLDERS, 'offerExpiresAt'],
  voucher: ['clientName', 'clientSurname', 'voucherCode', 'voucherBalance', 'voucherExpiresAt'],
  package: ['clientName', 'clientSurname', 'packageName', 'packageExpiresAt', 'remainingPackageCredits'],
};

export function communicationPlaceholderGroup(event) {
  if (event.startsWith('VOUCHER_')) return 'voucher';
  if (event.startsWith('PACKAGE_')) return 'package';
  return event === 'WAITLIST_AVAILABILITY' ? 'waitlist' : 'appointment';
}

export function validCapabilities(value) {
  return value && Array.isArray(value.languages) && value.languages.length > 0
    && value.languages.every((language) => COMMUNICATION_LANGUAGES.includes(language))
    && typeof value.timeZone === 'string' && Array.isArray(value.languageFallback)
    && COMMUNICATION_CHANNELS.every((channel) => typeof value.channels?.[channel]?.ready === 'boolean')
    && Object.keys(COMMUNICATION_PLACEHOLDER_GROUPS).every((group) => Array.isArray(value.placeholders?.[group])
      && value.placeholders[group].every((key) => typeof key === 'string'));
}

export function validCommunicationRules(value) {
  return value && [['enabledEvents', COMMUNICATION_EVENTS], ['enabledChannels', COMMUNICATION_CHANNELS],
    ['eligibleBookingStatuses', ['OPTION', 'CONFIRMED', 'GUARANTEED']]].every(([key, allowed]) =>
    Array.isArray(value[key]) && value[key].every((item) => allowed.includes(item)))
    && Object.entries(COMMUNICATION_RULE_LIMITS).every(([key, [min, max]]) => typeof value[key] === 'number'
      && Number.isFinite(value[key]) && value[key] >= min && value[key] <= max
      && (key === 'packageLowCreditThreshold' || Number.isInteger(value[key])));
}

export function communicationSendPayload(targetType, targetId, event, channel) {
  const id = Number(targetId);
  return COMMUNICATION_TARGETS[targetType]?.includes(event) && Number.isSafeInteger(id) && id > 0
    && channel === 'EMAIL' ? { [targetType]: id, event, channel } : null;
}

export function communicationResult(value) {
  const status = value?.status;
  return COMMUNICATION_STATUSES.includes(status) && Number.isInteger(value.deliveryId ?? value.id)
    ? { status, id: value.deliveryId ?? value.id, reason: value.reason || value.errorCode || null } : null;
}

export function communicationStatusColor(status) {
  return status === 'SENT' ? 'success' : status === 'FAILED' ? 'error'
    : ['SKIPPED', 'UNAVAILABLE'].includes(status) ? 'warning' : 'default';
}

export function templatePayload(selection, draft) {
  return { ...selection, subject: selection.channel === 'EMAIL' ? draft.subject.trim() : null,
    body: draft.body.trim(), active: draft.active };
}

export function validTemplate(value) {
  return value && COMMUNICATION_EVENTS.includes(value.event) && COMMUNICATION_CHANNELS.includes(value.channel)
    && COMMUNICATION_LANGUAGES.includes(value.language) && typeof value.body === 'string'
    && value.body.trim().length > 0 && value.body.length <= 20000 && typeof value.active === 'boolean'
    && (value.channel !== 'EMAIL' || (typeof value.subject === 'string'
      && value.subject.trim().length > 0 && value.subject.length <= 300));
}

export function previewTemplate(text, values) {
  // React renders this as plain text; never interpret template bodies as HTML.
  return String(text || '').replace(/\{\{(\w+)\}\}/g, (match, key) =>
    Object.values(COMMUNICATION_PLACEHOLDER_GROUPS).some((keys) => keys.includes(key)) && Object.prototype.hasOwnProperty.call(values, key)
      ? String(values[key]) : match);
}
